import React, { useEffect, useState } from "react";
import { RefreshCw, Shield, Waves, Radio } from "lucide-react";
import { getLogs } from "../lib/api";

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | anchor | drift | pulse | clean

  const load = async () => {
    setLoading(true);
    try {
      const data = await getLogs(200, 0);
      setLogs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = logs.filter((l) => {
    if (filter === "anchor") return l.anchor_blocked;
    if (filter === "drift") return l.drift_detected;
    if (filter === "pulse") return l.pulse_triggered;
    if (filter === "clean")
      return !l.anchor_blocked && !l.drift_detected;
    return true;
  });

  const tabs = [
    { id: "all", label: "All", testid: "filter-all" },
    { id: "anchor", label: "Anchor", testid: "filter-anchor" },
    { id: "drift", label: "Drift", testid: "filter-drift" },
    { id: "pulse", label: "Pulse", testid: "filter-pulse" },
    { id: "clean", label: "Clean", testid: "filter-clean" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            Interceptor
          </div>
          <h1 className="font-heading text-4xl mt-1">Transmission Logs</h1>
          <div className="text-xs text-zinc-500 mt-2">
            Every exchange that passed through the Sentinel wrapper.
          </div>
        </div>
        <button
          onClick={load}
          data-testid="refresh-logs-btn"
          className="inline-flex items-center gap-2 px-5 py-2 border border-zinc-700 text-xs uppercase tracking-[0.3em] hover:border-emerald-500 hover:text-emerald-400 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setFilter(t.id)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.3em] border transition-colors ${
              filter === t.id
                ? "border-emerald-500 text-emerald-400 bg-emerald-500/5"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
            }`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          {filtered.length} entries
        </span>
      </div>

      {loading && logs.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0b0b0d] p-12 text-center text-zinc-500 text-xs uppercase tracking-[0.3em]">
          Scanning the obsidian…
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="border border-zinc-800 bg-[#0b0b0d] p-12 text-center text-zinc-500 text-xs uppercase tracking-[0.3em]"
          data-testid="logs-empty-state"
        >
          No transmissions yet — open the Console to initiate a handshake.
        </div>
      ) : (
        <div className="border border-zinc-800" data-testid="logs-table">
          {filtered.map((l, idx) => (
            <div
              key={l.id}
              className={`grid grid-cols-1 md:grid-cols-12 gap-4 p-4 sm:p-5 ${
                idx !== filtered.length - 1 ? "border-b border-zinc-900" : ""
              } ${
                l.anchor_blocked
                  ? "bg-amber-500/[0.03]"
                  : l.drift_detected
                  ? "bg-red-500/[0.03]"
                  : ""
              }`}
              data-testid={`log-row-${l.id}`}
            >
              <div className="md:col-span-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                {fmtTime(l.timestamp)}
                <div className="mt-1 text-zinc-600 normal-case tracking-normal truncate">
                  {l.session_id.slice(0, 10)}…
                </div>
              </div>
              <div className="md:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                  Operator
                </div>
                <div className="text-xs text-zinc-200 whitespace-pre-wrap line-clamp-3">
                  {l.user_message}
                </div>
              </div>
              <div className="md:col-span-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                  Final Output
                </div>
                <div className="text-xs text-zinc-300 whitespace-pre-wrap line-clamp-4">
                  {l.final_output || <span className="text-zinc-600">—</span>}
                </div>
              </div>
              <div className="md:col-span-2 flex flex-row md:flex-col gap-1.5 md:items-end flex-wrap">
                {l.anchor_blocked && (
                  <span className="inline-flex items-center gap-1.5 border border-amber-500/50 text-amber-400 px-2 py-1 text-[9px] uppercase tracking-[0.25em]">
                    <Shield className="h-3 w-3" /> Anchor · {l.anchor_layer}
                  </span>
                )}
                {l.drift_detected && (
                  <span className="inline-flex items-center gap-1.5 border border-red-500/50 text-red-400 px-2 py-1 text-[9px] uppercase tracking-[0.25em]">
                    <Waves className="h-3 w-3" /> Drift
                  </span>
                )}
                {l.pulse_triggered && (
                  <span className="inline-flex items-center gap-1.5 border border-emerald-500/50 text-emerald-400 px-2 py-1 text-[9px] uppercase tracking-[0.25em]">
                    <Radio className="h-3 w-3" /> Pulse
                  </span>
                )}
                {!l.anchor_blocked && !l.drift_detected && !l.pulse_triggered && (
                  <span className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-400 px-2 py-1 text-[9px] uppercase tracking-[0.25em]">
                    Clean
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                  {l.tokens} tok
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
